import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTruck,
  faTimes,
  faCheckCircle,
  faMagic,
} from '@fortawesome/free-solid-svg-icons';
import './manual-assignment-modal.css';

/**
 * Modale pour l'affectation manuelle d'une commande à un camion
 */
export default function ManualAssignmentModal({
  isOpen,
  onClose,
  delivery,
  trucks,
  onAssign,
  isAssigning,
  autoOptimize = true,
  onAutoOptimizeChange,
}) {
  if (!isOpen) return null;

  const deliveryWeight = delivery?.weight || 0;

  /**
   * Calcule le taux de remplissage actuel et prévisionnel pour un camion
   */
  const calculateFillRates = (truck) => {
    const currentWeight = truck.usedCapacity || 0;
    const capacity = truck.capacity || 1;
    const currentRate = (currentWeight / capacity) * 100;
    const projectedWeight = currentWeight + deliveryWeight;
    const projectedRate = (projectedWeight / capacity) * 100;

    return {
      currentWeight,
      projectedWeight,
      capacity,
      currentRate: Math.min(currentRate, 100),
      projectedRate: Math.min(projectedRate, 100),
      isOverCapacity: projectedWeight > capacity,
    };
  };

  /**
   * Détermine la couleur de la jauge en fonction du taux de remplissage
   */
  const getGaugeColor = (rate, isOverCapacity) => {
    if (isOverCapacity) return '#dc3545'; // Rouge
    if (rate >= 90) return '#ff9800'; // Orange
    if (rate >= 70) return '#ffc107'; // Jaune
    return '#079992'; // Vert (couleur primaire)
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        {/* En-tête */}
        <div className="modal-header">
          <div>
            <h2 className="modal-title">Affecter manuellement la commande</h2>
            <p className="modal-subtitle">
              Client: <strong>{delivery?.client}</strong> - Poids:{' '}
              <strong>{deliveryWeight} kg</strong>
            </p>
          </div>
          <button
            className="modal-close-btn"
            onClick={onClose}
            disabled={isAssigning}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Corps de la modale */}
        <div className="modal-body">
          {/* Option d'optimisation automatique */}
          {onAutoOptimizeChange && (
            <div className="optimization-option">
              <label className="optimization-checkbox-label">
                <input
                  type="checkbox"
                  checked={autoOptimize}
                  onChange={(e) => onAutoOptimizeChange(e.target.checked)}
                  disabled={isAssigning}
                  className="optimization-checkbox"
                />
                <div className="optimization-label-content">
                  <div className="optimization-label-main">
                    <FontAwesomeIcon
                      icon={faMagic}
                      className="optimization-icon"
                    />
                    <span>Optimiser automatiquement le trajet du camion</span>
                  </div>
                  <div className="optimization-label-description">
                    Réorganise l'ordre des livraisons pour minimiser la distance
                    et le temps de trajet
                  </div>
                </div>
              </label>
            </div>
          )}

          {trucks.length === 0 ? (
            <div className="empty-state">
              <p>Aucun camion disponible</p>
            </div>
          ) : (
            <div className="trucks-list-view">
              {trucks.map((truck) => {
                const fillRates = calculateFillRates(truck);
                const gaugeColor = getGaugeColor(
                  fillRates.projectedRate,
                  fillRates.isOverCapacity
                );

                return (
                  <div
                    key={truck.id}
                    className={`truck-row ${isAssigning ? 'disabled' : ''} ${
                      fillRates.isOverCapacity ? 'over-capacity' : ''
                    }`}
                    onClick={() => !isAssigning && onAssign(truck)}
                  >
                    {/* Section gauche: Info du camion */}
                    <div className="truck-row-left">
                      <div
                        className="truck-icon-small"
                        style={{ color: truck.color || '#079992' }}
                      >
                        <FontAwesomeIcon icon={faTruck} size="lg" />
                      </div>
                      <div className="truck-info-compact">
                        <h3 className="truck-name-compact">
                          {truck.licensePlate}
                        </h3>
                        <p className="truck-type-compact">{truck.type}</p>
                      </div>
                    </div>

                    {/* Section centre: Jauges */}
                    <div className="truck-row-center">
                      {/* Jauge actuelle */}
                      <div className="gauge-inline">
                        <div className="gauge-inline-label">
                          <span className="gauge-label-text">Actuel</span>
                          <span className="gauge-value-text">
                            {fillRates.currentWeight.toFixed(0)} /{' '}
                            {fillRates.capacity} kg
                            <span className="gauge-percentage-inline">
                              ({fillRates.currentRate.toFixed(0)}%)
                            </span>
                          </span>
                        </div>
                        <div className="gauge-bar-inline">
                          <div
                            className="gauge-fill-inline"
                            style={{
                              width: `${fillRates.currentRate}%`,
                              backgroundColor: getGaugeColor(
                                fillRates.currentRate,
                                false
                              ),
                            }}
                          />
                        </div>
                      </div>

                      {/* Jauge prévisionnelle */}
                      <div className="gauge-inline">
                        <div className="gauge-inline-label">
                          <span className="gauge-label-text">Prévisionnel</span>
                          <span className="gauge-value-text">
                            {fillRates.projectedWeight.toFixed(0)} /{' '}
                            {fillRates.capacity} kg
                            <span
                              className="gauge-percentage-inline"
                              style={{ color: gaugeColor }}
                            >
                              ({fillRates.projectedRate.toFixed(0)}%)
                            </span>
                          </span>
                        </div>
                        <div className="gauge-bar-inline">
                          <div
                            className="gauge-fill-inline"
                            style={{
                              width: `${fillRates.projectedRate}%`,
                              backgroundColor: gaugeColor,
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Section droite: Indicateur de capacité */}
                    <div className="truck-row-right">
                      {fillRates.isOverCapacity ? (
                        <div className="capacity-badge warning">
                          <span>⚠️ Dépassement</span>
                        </div>
                      ) : fillRates.projectedRate >= 90 ? (
                        <div className="capacity-badge almost-full">
                          <FontAwesomeIcon icon={faCheckCircle} />
                          <span>Quasi-plein</span>
                        </div>
                      ) : (
                        <div className="capacity-badge available">
                          <FontAwesomeIcon icon={faCheckCircle} />
                          <span>Disponible</span>
                        </div>
                      )}
                      {(!truck.timeWindowStart || !truck.timeWindowEnd) && (
                        <div
                          className="capacity-badge"
                          title="Ce véhicule n'a pas d'horaires de départ/fin définis. Les ETAs calculés seront incorrects."
                          style={{
                            backgroundColor: '#fef3cd',
                            color: '#856404',
                            border: '1px solid #ffc107',
                            marginTop: '4px',
                          }}
                        >
                          <span>⚠ Sans horaires</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pied de page */}
        <div className="modal-footer">
          <button
            className="btn-secondary"
            onClick={onClose}
            disabled={isAssigning}
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}
